import { ContractEnums } from '@customer-mono/shared-types';
import { Box, Stack, IconButton } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { colors, CustomPagination, theme } from '../../components';
import ButtonComponent from '../../components/button/ButtonComponent';
import { LinkTextWrapper } from '../../components/global-styles/GlobalStyles';
import { CustomSelect } from '../../components/select/CustomSelect';
import CommonTabs from '../../components/tabComponent/commonTabComponent';
import { TextFieldComponent } from '../../components/text-field/text-field-component';
import CommonHeader from '../../components/ui-components/CommonHeader';
import { ContractApiResponse } from '../../services/contract-services';
import {
    getOpenInvoices,
    getScheduledPayments,
    getUserBasedPaymentHistory,
    OpenInvoiceApiResponse,
    PaymentHistoryItemNew,
} from '../../services/payment-services';
import { RootState } from '../../store';
import { hideLoader } from '../../store/reducers/appSlice';
import { openToast } from '../../store/reducers/toastSlice';
import { canMakePaymentForContract, formatToUSDate } from '../../utils/helper';
import { NavigationConfig } from '../../utils/navigation-constants';
import { useTranslation } from '../../utils/translations';

import PaymentHistory from './payment-history';
import SchedulePayment from './schedule-payment';
import UpcomingPayments from './upcoming-payments';

interface ScheduledPaymentItem {
    Id?: string | number;
    PaymentDate?: string;
    DueDate?: string;
    isScheduledPayment?: boolean;
    Status?: string | number;
    UserId?: string;
    AspireContractId?: string;
    aspireContractId?: string;
    ContractId?: string;
    contractId?: string;
    Amount?: string | number;
    Platform?: string | number;
    ResponseMessage?: string;
    DisputedPayment?: boolean;
    ContractAspireId?: string;
}

export const PaymentsOverview: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { contracts } = useSelector((state: RootState) => state.contracts);
    const [paymentData, setPaymentData] = useState<PaymentHistoryItemNew[]>([]);
    const location = useLocation();

    // Active tab state (sync from location when navigating to this page with state.activeTab).
    // Only re-sync when location.state.activeTab itself changes (i.e. a fresh navigation), not on
    // every activeTab change — otherwise this effect fights the user's own tab clicks, since
    // location.state stays the same object for as long as they remain on this route.
    const [activeTab, setActiveTab] = useState(location?.state?.activeTab ?? 0);
    const appliedTabFromStateRef = useRef(location?.state?.activeTab);
    useEffect(() => {
        const tabFromState = location?.state?.activeTab;
        if (typeof tabFromState === 'number' && tabFromState >= 0 && tabFromState !== appliedTabFromStateRef.current) {
            appliedTabFromStateRef.current = tabFromState;
            setActiveTab(tabFromState);
        }
    }, [location?.state?.activeTab]);

    const [upcomingPaymentsData, setUpcomingPaymentsData] = useState<OpenInvoiceApiResponse[]>([]);
    const [pendingScheduleAmount, setPendingScheduleAmount] = useState<
        Array<{ contractId: string; totalPendingScheduleAmount: number }>
    >([]);
    const [scheduledPaymentsData, setScheduledPaymentsData] = useState<ScheduledPaymentItem[]>([]);
    const [isLoadingScheduledPayments, setIsLoadingScheduledPayments] = useState(true);
    const initialOpenInvoicesFetch = useRef(false);
    const initialPaymentHistoryFetch = useRef(false);
    const initialPaymentsFetch = useRef(false);

    // Upcoming Payments Pagination State
    const [upcomingCurrentPage, setUpcomingCurrentPage] = useState<number>(0);
    const [upcomingRowsPerPage, setUpcomingRowsPerPage] = useState<number>(10);
    const [upcomingTotalCount, setUpcomingTotalCount] = useState<number>(0);

    // Schedule Payment Pagination State
    const [scheduleCurrentPage, setScheduleCurrentPage] = useState<number>(0);
    const [scheduleRowsPerPage, setScheduleRowsPerPage] = useState<number>(10);
    const [scheduleTotalCount, setScheduleTotalCount] = useState<number>(0);

    // Payment History Pagination State
    const [historyCurrentPage, setHistoryCurrentPage] = useState<number>(0);
    const [historyRowsPerPage, setHistoryRowsPerPage] = useState<number>(10);
    const [historyTotalCount, setHistoryTotalCount] = useState<number>(0);

    const fetchOpenInvoices = useCallback(async () => {
        try {
            const response = await getOpenInvoices(true);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                setUpcomingPaymentsData(response.data);
                setPendingScheduleAmount(
                    Array.isArray(response.pendingScheduleAmount)
                        ? response.pendingScheduleAmount.map(
                              (item: {
                                  contractId: string | number;
                                  totalPendingScheduleAmount?: number | string | null;
                              }) => ({
                                  contractId: String(item.contractId),
                                  totalPendingScheduleAmount: Number(item.totalPendingScheduleAmount ?? 0),
                              }),
                          )
                        : [],
                );
            } else {
                setUpcomingPaymentsData([]);
                setPendingScheduleAmount([]);
            }
        } catch (err) {
            console.error('Error fetching open invoices:', err);
            const e = err as { message?: string };
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: e.message,
                    severity: 'error',
                }),
            );
            setUpcomingPaymentsData([]);
            setPendingScheduleAmount([]);
        }
    }, [dispatch]);

    // Fetch open invoices on component mount
    useEffect(() => {
        if (initialOpenInvoicesFetch.current) return;
        initialOpenInvoicesFetch.current = true;

        fetchOpenInvoices();
    }, [fetchOpenInvoices]);

    // Fetch scheduled payments
    const fetchScheduledPayments = useCallback(async () => {
        setIsLoadingScheduledPayments(true);
        try {
            const response = await getScheduledPayments(true);
            if (response.statusCode === 200 && Array.isArray(response.data)) {
                setScheduledPaymentsData(
                    response?.data?.map((item: OpenInvoiceApiResponse) => ({
                        ...item,
                        DueDate: (item as unknown as { PaymentDate?: string }).PaymentDate,
                        isScheduledPayment: true,
                    })) || [],
                );
            } else {
                setScheduledPaymentsData([]);
            }
        } catch (err) {
            console.error('Error fetching scheduled payments:', err);
            const e = err as { message?: string };
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message: e.message,
                    severity: 'error',
                }),
            );
            setScheduledPaymentsData([]);
        } finally {
            dispatch(hideLoader());
            setIsLoadingScheduledPayments(false);
        }
    }, [dispatch]);

    // Fetch scheduled payments on component mount
    useEffect(() => {
        if (initialPaymentsFetch.current) return;
        initialPaymentsFetch.current = true;
        fetchScheduledPayments();
    }, [fetchScheduledPayments]);

    // Fetch payment history on component mount
    useEffect(() => {
        if (initialPaymentHistoryFetch.current) return;
        initialPaymentHistoryFetch.current = true;

        const fetchPaymentHistory = async () => {
            try {
                const response = await getUserBasedPaymentHistory(true);
                if (response.statusCode === 200 && Array.isArray(response.data)) {
                    setPaymentData(response.data);
                } else {
                    setPaymentData([]);
                }
            } catch (err) {
                console.error('Error fetching payment history:', err);
                const e = err as { message?: string };
                dispatch(
                    openToast({
                        show: true,
                        isAlertOpen: true,
                        message: e.message,
                        severity: 'error',
                    }),
                );
                setPaymentData([]);
            } finally {
                dispatch(hideLoader());
            }
        };

        fetchPaymentHistory();
    }, [dispatch]);

    // Filter to only active contracts
    const activeContracts = useMemo(
        () =>
            contracts.filter(
                (contractItem: ContractApiResponse) =>
                    contractItem.Status === ContractEnums?.ContractStatusTypes?.active,
            ),
        [contracts],
    );

    // Contract filter — initialize from navigation state if provided (e.g. from "Show in Scheduled payments" link)
    const [selectedContractFilter, setSelectedContractFilter] = useState<string>(
        typeof location?.state?.contractFilter === 'string' ? location.state.contractFilter : '',
    );
    const prevPathnameRef = useRef(location.pathname);

    // Reset filter when navigating back to payments from a different page, unless a contractFilter was provided
    useEffect(() => {
        const isOnPayments = location.pathname === NavigationConfig.Payments;
        const wasOnPayments = prevPathnameRef.current === NavigationConfig.Payments;
        if (isOnPayments && !wasOnPayments && !location?.state?.contractFilter) {
            setSelectedContractFilter('');
        }
        prevPathnameRef.current = location.pathname;
    }, [location.pathname, location?.state?.contractFilter]);

    // Build dropdown from Redux active contracts + any contract that appears in payment data
    // so the list shows all contracts (even if Redux list was not yet loaded or is incomplete)
    const contractFilterOptions = useMemo(() => {
        const byId = new Map<string, { id: string; name: string }>();
        // Add from Redux active contracts first (so we get AspireId + AssetDesc when available)
        activeContracts.forEach((c: ContractApiResponse) => {
            const id = String(c.AspireId ?? c.Id ?? '');
            if (!id) return;
            const name =
                `${c.AspireId ?? ''} ${c.AssetDesc ? '· ' + (c.AssetDesc || '').trim() : ''}`.trim() ||
                String(c.AspireId ?? c.Id);
            byId.set(id, { id, name });
        });
        // Add any contract from open invoices not already in the map
        (upcomingPaymentsData || []).forEach((inv: OpenInvoiceApiResponse) => {
            const id = String(
                inv?.aspireContractId ?? inv?.AspireContractId ?? inv?.ContractId ?? inv?.contractId ?? '',
            );
            if (!id || byId.has(id)) return;
            byId.set(id, { id, name: id });
        });
        // Add any contract from scheduled payments
        (scheduledPaymentsData || []).forEach((item: ScheduledPaymentItem) => {
            const id = String(
                item?.AspireContractId ?? item?.aspireContractId ?? item?.ContractId ?? item?.contractId ?? '',
            );
            if (!id || byId.has(id)) return;
            byId.set(id, { id, name: id });
        });
        // Include contract IDs from payment history so filter dropdown matches API row keys
        (paymentData || []).forEach((p: PaymentHistoryItemNew) => {
            const id = String(p?.contractId ?? '').trim();
            if (!id || byId.has(id)) return;
            byId.set(id, { id, name: id });
        });
        const options = Array.from(byId.values()).sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true }),
        );
        return [{ id: '', name: t('payments.filter.allContracts') || 'All contracts' }, ...options].filter(
            opt => opt.id !== undefined && opt.id !== null,
        );
    }, [activeContracts, upcomingPaymentsData, scheduledPaymentsData, paymentData, t]);
    const filteredUpcomingPaymentsData = useMemo(() => {
        if (!selectedContractFilter) return upcomingPaymentsData;
        return upcomingPaymentsData.filter(
            (inv: OpenInvoiceApiResponse) =>
                String(inv?.aspireContractId ?? inv?.AspireContractId ?? '') === String(selectedContractFilter),
        );
    }, [upcomingPaymentsData, selectedContractFilter]);

    const filteredScheduledPaymentsData = useMemo(() => {
        if (!selectedContractFilter) return scheduledPaymentsData;
        const filterStr = String(selectedContractFilter).trim();
        const contract = contracts.find(
            (c: ContractApiResponse) => String(c.AspireId ?? '') === filterStr || String(c.Id ?? '') === filterStr,
        );
        const aspireId = contract ? String(contract.AspireId ?? '').trim() : filterStr;
        const dbId = contract && contract.Id != null ? String(contract.Id) : '';

        return scheduledPaymentsData.filter((item: ScheduledPaymentItem) => {
            const rowAspire = String(item?.AspireContractId ?? item?.aspireContractId ?? '').trim();
            const rowContractId = String(item?.ContractId ?? item?.contractId ?? '').trim();
            return (
                rowAspire === filterStr ||
                rowAspire === aspireId ||
                rowContractId === filterStr ||
                (dbId !== '' && rowContractId === dbId)
            );
        });
    }, [scheduledPaymentsData, selectedContractFilter, contracts]);

    const filteredPaymentData = useMemo(() => {
        if (!selectedContractFilter) return paymentData;
        const filterStr = String(selectedContractFilter).trim();
        const contract = contracts.find(
            (c: ContractApiResponse) => String(c.AspireId ?? '') === filterStr || String(c.Id ?? '') === filterStr,
        );
        const aspireId = contract ? String(contract.AspireId ?? '').trim() : filterStr;
        const dbId = contract && contract.Id != null ? String(contract.Id) : '';

        return paymentData.filter((item: PaymentHistoryItemNew) => {
            const cid = String(item?.contractId ?? '').trim();
            if (!cid) return false;
            return cid === filterStr || cid === aspireId || (dbId !== '' && cid === dbId);
        });
    }, [paymentData, selectedContractFilter, contracts]);

    // Global search (applies to all tabs)
    const [paymentsSearchTerm, setPaymentsSearchTerm] = useState('');
    const searchLower = paymentsSearchTerm.trim().toLowerCase();

    const searchFilteredUpcomingPaymentsData = useMemo(() => {
        if (!searchLower) return filteredUpcomingPaymentsData;
        return filteredUpcomingPaymentsData.filter((inv: OpenInvoiceApiResponse) => {
            // Only match on visible table fields: Contract ID, Invoice Due Date, Invoice Number, Amount Due
            const contractId = String(
                inv?.aspireContractId ?? inv?.AspireContractId ?? inv?.ContractId ?? inv?.contractId ?? '',
            ).toLowerCase();
            const dueDate = (
                inv?.DueDate ? formatToUSDate(inv.DueDate) : inv?.duedate ? formatToUSDate(inv.duedate) : ''
            ).toLowerCase();
            const invoiceNumber = String(inv?.InvoiceNumber ?? inv?.invoiceNumber ?? '').toLowerCase();
            const amount = String(inv?.Amount ?? inv?.amount ?? '').toLowerCase();
            const visibleParts = [contractId, dueDate, invoiceNumber, amount];
            return visibleParts.some(part => part.includes(searchLower));
        });
    }, [filteredUpcomingPaymentsData, searchLower]);

    const searchFilteredScheduledPaymentsData = useMemo(() => {
        if (!searchLower) return filteredScheduledPaymentsData;
        return filteredScheduledPaymentsData.filter((item: ScheduledPaymentItem) => {
            // Only match on visible table fields: Contract ID, Scheduled date, Amount due
            const contractId = String(
                item?.AspireContractId ?? item?.aspireContractId ?? item?.ContractId ?? '',
            ).toLowerCase();
            const scheduledDate = (
                item?.DueDate ? formatToUSDate(item.DueDate) : item?.PaymentDate ? formatToUSDate(item.PaymentDate) : ''
            ).toLowerCase();
            const amount = String(item?.Amount ?? '').toLowerCase();
            const visibleParts = [contractId, scheduledDate, amount];
            return visibleParts.some(part => part.includes(searchLower));
        });
    }, [filteredScheduledPaymentsData, searchLower]);

    const searchFilteredPaymentData = useMemo(() => {
        if (!searchLower) return filteredPaymentData;
        return filteredPaymentData.filter((item: PaymentHistoryItemNew) => {
            const contractId = String(item?.contractId ?? '').toLowerCase();
            const paymentDate = (item?.paymentDate ? formatToUSDate(item.paymentDate) : '').toLowerCase();
            const amount = String(item?.amount ?? '').toLowerCase();
            const method = String(item?.paymentType ?? '').toLowerCase();
            const referenceNumber = String(item?.referenceNumber ?? '').toLowerCase();
            const visibleParts = [contractId, paymentDate, amount, method, referenceNumber];
            return visibleParts.some(part => part.includes(searchLower));
        });
    }, [filteredPaymentData, searchLower]);

    // Reset all tab pagination when contract filter or search changes
    useEffect(() => {
        setUpcomingCurrentPage(0);
        setScheduleCurrentPage(0);
        setHistoryCurrentPage(0);
    }, [selectedContractFilter, paymentsSearchTerm]);

    // Pay All button disabled when no contract can make payment (same logic as Pay Now: consumer loan can always pay; others need open invoices)
    const allContractsDisabled =
        activeContracts.length === 0 ||
        activeContracts.every(c => !canMakePaymentForContract(c, upcomingPaymentsData ?? [], activeContracts));

    // Tab change handler - reset pagination when switching tabs
    const handleTabChange = (index: number) => {
        setActiveTab(index);
        if (index === 0) {
            fetchOpenInvoices();
        }
        // Reset pagination for all tabs when switching
        setUpcomingCurrentPage(0);
        setScheduleCurrentPage(0);
        setHistoryCurrentPage(0);
    };

    // Upcoming Payments Pagination Handlers
    const handleUpcomingPageChange = (newPage: number, newRowsPerPage: number) => {
        setUpcomingCurrentPage(newPage);
        setUpcomingRowsPerPage(newRowsPerPage);
    };

    const handleUpcomingRowsPerPageChange = (newRowsPerPage: number) => {
        setUpcomingCurrentPage(0);
        setUpcomingRowsPerPage(newRowsPerPage);
    };

    // Schedule Payment Pagination Handlers
    const handleSchedulePageChange = (newPage: number, newRowsPerPage: number) => {
        setScheduleCurrentPage(newPage);
        setScheduleRowsPerPage(newRowsPerPage);
    };

    const handleScheduleRowsPerPageChange = (newRowsPerPage: number) => {
        setScheduleCurrentPage(0);
        setScheduleRowsPerPage(newRowsPerPage);
    };

    // Payment History Pagination Handlers
    const handleHistoryPageChange = (newPage: number, newRowsPerPage: number) => {
        setHistoryCurrentPage(newPage);
        setHistoryRowsPerPage(newRowsPerPage);
    };

    const handleHistoryRowsPerPageChange = (newRowsPerPage: number) => {
        setHistoryCurrentPage(0);
        setHistoryRowsPerPage(newRowsPerPage);
    };

    const tabItems = [
        {
            label: t('payments.tabs.upcomingPayments'),
            content: (
                <UpcomingPayments
                    currentPage={upcomingCurrentPage}
                    rowsPerPage={upcomingRowsPerPage}
                    onTotalCountChange={setUpcomingTotalCount}
                    invoiceData={searchFilteredUpcomingPaymentsData}
                    pendingScheduleAmount={pendingScheduleAmount}
                    scheduledPaymentsData={scheduledPaymentsData}
                    onNavigateToScheduledPayments={contractId => {
                        setActiveTab(1);
                        if (contractId) {
                            setSelectedContractFilter(contractId);
                        }
                    }}
                />
            ),
        },
        {
            label: t('payments.tabs.schedulePayment'),
            content: (
                <SchedulePayment
                    currentPage={scheduleCurrentPage}
                    rowsPerPage={scheduleRowsPerPage}
                    onTotalCountChange={setScheduleTotalCount}
                    scheduledPaymentsData={searchFilteredScheduledPaymentsData}
                    fetchScheduledPayments={fetchScheduledPayments}
                    isLoadingData={isLoadingScheduledPayments}
                />
            ),
        },
        {
            label: t('payments.tabs.paymentHistory'),
            content: (
                <PaymentHistory
                    currentPage={historyCurrentPage}
                    rowsPerPage={historyRowsPerPage}
                    onTotalCountChange={setHistoryTotalCount}
                    paymentData={searchFilteredPaymentData}
                />
            ),
        },
    ];

    const handlePayAll = () => {
        if (activeContracts.length > 0) {
            const allContractIds = activeContracts.map(
                (contractItem: ContractApiResponse) => contractItem.AspireId || `Contract ${contractItem.Id}`,
            );
            navigate(NavigationConfig.DashboardMakePayment, {
                state: { selectedContractIds: allContractIds, payAll: true, fromPayments: true },
            });
        }
    };

    // Get current pagination based on active tab
    const renderPagination = () => {
        if (activeTab === 0 && upcomingTotalCount > 0) {
            return (
                <CustomPagination
                    totalCount={upcomingTotalCount}
                    rowsPerPage={upcomingRowsPerPage}
                    currentPage={upcomingCurrentPage}
                    onPageChange={handleUpcomingPageChange}
                    onRowsPerPageChange={handleUpcomingRowsPerPageChange}
                    serverSide={false}
                    sortedRowsLength={upcomingTotalCount}
                />
            );
        }
        if (activeTab === 1 && scheduleTotalCount > 0) {
            return (
                <CustomPagination
                    totalCount={scheduleTotalCount}
                    rowsPerPage={scheduleRowsPerPage}
                    currentPage={scheduleCurrentPage}
                    onPageChange={handleSchedulePageChange}
                    onRowsPerPageChange={handleScheduleRowsPerPageChange}
                    serverSide={false}
                    sortedRowsLength={scheduleTotalCount}
                    isSchedulePaymentNote={false}
                />
            );
        }
        if (activeTab === 2 && historyTotalCount > 0) {
            return (
                <CustomPagination
                    totalCount={historyTotalCount}
                    rowsPerPage={historyRowsPerPage}
                    currentPage={historyCurrentPage}
                    onPageChange={handleHistoryPageChange}
                    onRowsPerPageChange={handleHistoryRowsPerPageChange}
                    serverSide={false}
                    sortedRowsLength={historyTotalCount}
                />
            );
        }
        return null;
    };

    return (
        <Stack gap={3}>
            <Stack pt={3}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mx={3}
                    flexWrap="wrap"
                    gap={2}
                >
                    <Box sx={{ minWidth: 0, flex: { xs: '1 1 0%', lg: '0 1 auto' } }}>
                        <CommonHeader
                            mainTitle={t('payments.header.mainTitle')}
                            subTitle={t('payments.header.subTitle')}
                            icon="icon-loan"
                        />
                    </Box>
                    <Stack
                        width={{ xs: 'fit-content', sm: 'fit-content' }}
                        flexShrink={0}
                        alignItems={'end'}
                        sx={{ display: { xs: 'flex', lg: 'none' } }}
                    >
                        <ButtonComponent
                            variant="contained"
                            sx={{
                                width: 'fit-content',
                            }}
                            onClick={handlePayAll}
                            disabled={allContractsDisabled}
                        >
                            {t('dashboard.header.makePayment')}
                        </ButtonComponent>
                    </Stack>
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={2}
                        flexWrap="wrap"
                        width={{ xs: '100%', lg: 'auto' }}
                    >
                        {selectedContractFilter ? (
                            <LinkTextWrapper
                                onClick={() => setSelectedContractFilter('')}
                                sx={{
                                    ...theme.typography.h6,
                                    color: colors.errorPreset[10],
                                    '&:hover': { color: colors.errorPreset[10] },
                                }}
                            >
                                {t('payments.filter.reset') || 'Reset'}
                            </LinkTextWrapper>
                        ) : null}
                        <Box width={{ xs: 'auto', sm: 220 }} flexGrow={{ xs: 1, sm: 0 }} minWidth={0}>
                            <CustomSelect
                                value={selectedContractFilter}
                                onChange={value => setSelectedContractFilter(value)}
                                options={contractFilterOptions}
                                placeholder={t('payments.createPayment.selectContract') || 'Select contract'}
                                noOptionsMessage={t('payments.filter.noContracts') || 'No contracts'}
                                renderContractItemStyle
                            />
                        </Box>
                        <Box
                            width={{ xs: 'auto', sm: 'auto', lg: 300 }}
                            flexGrow={{ xs: 1, sm: 1, lg: 0 }}
                            minWidth={0}
                        >
                            <TextFieldComponent
                                value={paymentsSearchTerm}
                                onChange={e => setPaymentsSearchTerm(e.target.value)}
                                placeholder={
                                    t('payments.filter.searchPlaceholder') || 'Search contract, invoice, amount...'
                                }
                                ariaLabel={t('payments.filter.searchPlaceholder') || 'Search payments'}
                                startIcon={<span className="icon-search" style={{ fontSize: 16, color: 'inherit' }} />}
                                endIcon={
                                    paymentsSearchTerm ? (
                                        <IconButton
                                            size="small"
                                            onClick={() => setPaymentsSearchTerm('')}
                                            aria-label={t('payments.filter.clearSearch') || 'Clear search'}
                                            sx={{ p: 0.5 }}
                                        >
                                            <span className="icon-close" style={{ fontSize: 16 }} />
                                        </IconButton>
                                    ) : undefined
                                }
                                sx={{ width: '100%' }}
                                className="searchTextFiled"
                            />
                        </Box>
                        <Stack
                            width={{ xs: 'fit-content', sm: 'fit-content' }}
                            flexShrink={0}
                            alignItems={'end'}
                            sx={{ display: { xs: 'none', lg: 'flex' } }}
                        >
                            <ButtonComponent
                                variant="contained"
                                sx={{
                                    width: 'fit-content',
                                }}
                                onClick={handlePayAll}
                                disabled={allContractsDisabled}
                            >
                                {t('dashboard.header.makePayment')}
                            </ButtonComponent>
                        </Stack>
                    </Stack>
                </Stack>

                <Stack
                    mt={3}
                    sx={{
                        '& .tabHeader': {
                            mx: 3,
                        },
                    }}
                >
                    <CommonTabs tabs={tabItems} activeIndex={activeTab} onTabChange={handleTabChange} />
                </Stack>
            </Stack>

            {/* Pagination - rendered based on active tab */}
            {renderPagination()}
        </Stack>
    );
};
